import {create, StateMachine} from 'javascript-state-machine';

import type {HeartbeatService} from '../core/HeartbeatService';
import {Event, JSM_INITIAL_EVENT} from '../enums/Event';
import {createVideoStartFailedReason, VideoStartFailedReason} from '../enums/VideoStartFailedReason';
import type {AnalyticsEventBase, CustomDataValuesEvent, ErrorEvent, QualityChangeEvent} from '../types/EventData';
import type {StateMachineCallbacks} from '../types/StateMachineCallbacks';

import {AnalyticsStateMachine} from './AnalyticsStateMachine';
import {
  customStateMachineErrorCallback,
  getAllStatesBut,
  logStateMachineTransition,
  whileIn,
} from './stateMachineUtils';

export enum State {
  // handles ads in the middle of playback (midroll & postroll ads)
  AD_PLAYBACK = 'AD_PLAYBACK',
  // handles ad that occur during startup aka preroll ads
  AD_PLAYBACK_IN_STARTUP = 'AD_PLAYBACK_IN_STARTUP',

  /**
   * We track and consider the buffering state only when it comes from the {@link State.PLAYING},
   * aka. when the video was playing and stopped because it started to buffer.
   */
  BUFFERING = 'BUFFERING',
  /**
   * This state was created only to be able to "cut" the {@link State.PLAYING} and {@link State.PAUSE}
   * and send playing/pause sample with current custom data, while calling {@link AdapterAPI.setCustomData} function
   * and setting the new custom data values.
   *
   * @remarks
   *
   * Our implementation relays on a specific state name "CUSTOMDATACHANGE" because it should call the
   * default "onleavestate" state machine callback {@link stateMachineCallbacks.customdatachange}.
   */
  CUSTOMDATACHANGE = 'CUSTOMDATACHANGE',
  ERROR = 'ERROR',
  PAUSE = 'PAUSE',
  PLAYING = 'PLAYING',
  QUALITY_CHANGE = 'QUALITY_CHANGE',
  READY = 'READY',
  /**
   * We track and consider the "seeking" state only when it comes from the {@link State.PLAYING},
   * aka. when the video has already started to play and is already playing.
   */
  SEEKING = 'SEEKING',
  SETUP = 'SETUP',
  SOURCE_CHANGING = 'SOURCE_CHANGING',
  STARTUP = 'STARTUP',
  UNLOADED = 'UNLOADED',
  VIDEOSTART_FAILED = 'VIDEOSTART_FAILED',
}

export class THEOplayerAnalyticsStateMachine extends AnalyticsStateMachine {
  constructor(stateMachineCallbacks: StateMachineCallbacks, playingHeartbeatService: HeartbeatService) {
    super(stateMachineCallbacks, playingHeartbeatService);
  }

  override createStateMachine(): StateMachine {
    return create({
      // custom initial state and custom JSM initial event name
      // initial event is used by JSM for the initial state transition 'none' -> 'SETUP'
      initial: {state: State.SETUP, event: JSM_INITIAL_EVENT},
      error: customStateMachineErrorCallback,
      events: [
        {name: Event.READY, from: State.SETUP, to: State.READY},

        {name: Event.PLAY, from: State.READY, to: State.STARTUP},
        // Fallback transition: to handle a missing "play" event and a missing transition from READY to STARTUP
        //
        // Case: MISSING "PLAY" EVENT FROM A PLAY ATTEMPT DURING SLOW SOURCE CHANGE.
        // Problem:
        //    When the source change was triggered, the player started to load the video (probably on slow network),
        //    and we were in the SOURCE_CHANGING state. But when `player.play()` was called during that state, the player
        //    did not fire "play" (player bug?), so this play attempt was not recognized by us, and we have not
        //    transitioned to the STARTUP state. Once the source was loaded, we transitioned to the READY state, but
        //    the player remembered the play attempt from before, so the video actually started to play. But because
        //    of the missing transition to STARTUP, we got stuck in the READY state.
        // Solution:
        //    If we are still in the READY state and there was a "playing" event, it means that the play attempt was not
        //    recognized by us, but the video actually started to play, so we will immediately transition to the PLAYING
        //    state. But before that, we will trigger the "startup" sample with a fixed duration of 1ms, when leaving
        //    the READY state.
        // Found on:
        //    theoplayer@10.10.0
        {name: Event.PLAYING, from: State.READY, to: State.PLAYING},

        // Case: PRECEDING "PAUSE" EVENT ON STARTUP ERRORS
        // Problem:
        //    In case of a player "error" event during the STARTUP state, we see that the player fires a "pause" event
        //    automatically before the actual "error" event. This messes with our state machine, to not be able to track
        //    the error as a startup failure, because such the preceding PAUSE event would first get us into the PAUSE
        //    state, where the following "error" event would be considered as a normal error during the PAUSE state.
        // Solution:
        //    To handle this case, we delay the PAUSE event execution to see if an ERROR event is fired right after, and
        //    if so, we give the ERROR event the priority to be executed first, and we cancel the delayed PAUSE event,
        //    so that the state machine can correctly track the startup failure. If there is no ERROR event fired right
        //    after, we execute the delayed PAUSE event as if nothing happened.
        {name: Event.ERROR, from: State.STARTUP, to: State.VIDEOSTART_FAILED},
        {name: Event.MANUAL_SOURCE_CHANGE, from: State.STARTUP, to: State.VIDEOSTART_FAILED},
        {name: Event.VIDEOSTART_TIMEOUT, from: State.STARTUP, to: State.VIDEOSTART_FAILED},

        {name: Event.READY, from: State.VIDEOSTART_FAILED, to: State.READY},

        // cover the case when a user double-click the play button.
        // aka. when after the first play click, a video starts to load, and we are in the startup state,
        // but during the loading a user clicks the play button again to pause the video
        {name: Event.PAUSE, from: State.STARTUP, to: State.READY},

        // We can't use a player 'timeupdate' event, because it is fired asap after the play button is hit
        // with currentTime: 0, just to set playback state to beginning / to initial state, and THEOplayer is
        // still loading the asset (a specific THEOplayer behavior)
        {name: Event.PLAYING, from: State.STARTUP, to: State.PLAYING},

        // Case: PREROLL AD
        // Problem:
        //    In the case of preroll Ads, they always start when we have already entered the STARTUP state.
        // Solution:
        //    We had to create a specific AD_PLAYBACK_IN_STARTUP state:
        //    - to not affect STARTUP state and the startup time calculation with the preroll ad time.
        //    - to be able to send an "ad" sample for preroll ads in onleaveSTATE callback.
        //    - to be able to return us back to STARTUP state after the preroll ad is finished, to continue the normal startup.
        {name: Event.START_AD, from: State.STARTUP, to: State.AD_PLAYBACK_IN_STARTUP},
        {name: Event.END_AD, from: State.AD_PLAYBACK_IN_STARTUP, to: State.STARTUP},

        // Case: MIDROLL & POSTROLL ADS DETERMINATION
        // Problem:
        //    Player event "adbreakbegin" for both midroll and postroll Ad, can theoretically happen at any of our states
        //    (PLAYING, SEEKING, maybe also BUFFERING etc.), and it is hard to determine to which state we should return
        //    back after the ad is finished. An additional problem to that is that we can't clearly detect a postroll ad,
        //    because the THEOplayer will start it before emitting a player "end" event, so in the same way as a midroll ad.
        // Solution:
        //    - We will transition to the AD_PLAYBACK state on "adbreakbegin" no matter in which state it happens, and when
        //      it ends, we will return to the PLAYING state. Why PLAYING? Because we could see THEOplayer behavior that
        //      after midroll ad (for postroll we did special handling), the video continues to play automatically no
        //      matter from what state Ads has started. For example, the use case, when we seeked, or paused and seeked
        //      the video over the Ad position.
        //    - For the postroll ads we have a special handling! We will rely on the fact that after the postroll ad has
        //      finished, THEOplayer will always emit a player "end" event (because playback is/was at the end of the video).
        //      So what we need to do is to always delay END_AD event execution! If we see that an END event is fired
        //      right after, it means that the ad that just finished was a postroll ad, so we will cancel the END_AD event
        //      and transition directly to the PAUSE state. If no END event follows, it means that it was a midroll ad,
        //      so we will execute the END_AD event as normal and transition back to PLAYING state.
        //      Without this, we would be tracking postroll ads as normal midroll ones, and we would be returning back to
        //      PLAYING state that would be tracked as "micro-short playing sample" as the player "end" event came soon.
        // Found on:
        //    theoplayer@10.12.0
        {name: Event.START_AD, from: getAllStatesBut([State.STARTUP], State), to: State.AD_PLAYBACK},
        {name: Event.END_AD, from: State.AD_PLAYBACK, to: State.PLAYING},
        // to support the case: MIDROLL & POSTROLL ADS DETERMINATION
        {name: Event.END, from: State.AD_PLAYBACK, to: State.PAUSE},

        {name: Event.START_BUFFERING, from: State.PLAYING, to: State.BUFFERING},
        {name: Event.PLAYING, from: State.BUFFERING, to: State.PLAYING},
        {name: Event.PAUSE, from: State.BUFFERING, to: State.PAUSE},

        {name: Event.PAUSE, from: State.PLAYING, to: State.PAUSE},
        {name: Event.PLAYING, from: State.PAUSE, to: State.PLAYING},
        // PAUSE state should be also interrupted with the "play" player event
        // to already start PLAYING state tracking with "attempt to play"
        {name: Event.PLAY, from: State.PAUSE, to: State.PLAYING},

        // Case: PRECEDING "PAUSE" EVENT BEFORE "SEEK" WHEN SEEKING VIA UI PROGRESS BAR
        // Problem:
        //    When the user seeks via the UI progress bar while the video is playing, THEOplayer fires a "pause" event
        //    immediately before the "seeking" event. This causes our state machine to transition from PLAYING → PAUSE,
        //    which sends a spurious "pause sample" and loses the seek tracking — the SEEK event is then ignored because
        //    the state machine is no longer in the PLAYING state. (Only PLAYING → SEEKING is a valid transition.)
        //
        //    That THEOplayer behavior is a feature for UI, not a bug! Response from THEOplayer team: Both default UI
        //    (based off video.js 5 UI) and the Web Open Video UI pause when scrubbing. This is done so that if you hover
        //    somewhere still while scrubbing without releasing, the player doesn't suddenly start playing again while
        //    you're still scrubbing, until the scrubbing action is stopped by releasing the mouse and the playback resumes.
        //
        //  Solution:
        //    To handle this case, we delay the PAUSE event to see if a SEEK event is fired right after. If a SEEK event
        //    arrives while the PAUSE is pending, we cancel the PAUSE and let the SEEK be dispatched normally, so the
        //    state machine correctly tracks PLAYING → SEEKING. If no SEEK follows, the PAUSE is dispatched as normal.
        {name: Event.SEEK, from: State.PLAYING, to: State.SEEKING},
        {name: Event.PLAYING, from: State.SEEKING, to: State.PLAYING},

        {name: Event.VIDEO_CHANGE, from: State.PLAYING, to: State.QUALITY_CHANGE},
        {name: Event.AUDIO_CHANGE, from: State.PLAYING, to: State.QUALITY_CHANGE},
        {name: Event.FINISH_QUALITY_CHANGE, from: State.QUALITY_CHANGE, to: State.PLAYING},

        {
          name: Event.MANUAL_SOURCE_CHANGE,
          from: getAllStatesBut([State.STARTUP], State),
          to: State.SOURCE_CHANGING,
        },
        // Case: ADAPTER API SOURCE CHANGE VS. PLAYER SOURCE CHANGE
        // Problem:
        //    The Event.MANUAL_SOURCE_CHANGE is fired in two cases:
        //    1) when the player source change is detected by InternalAdapter
        //    2) when the AdapterAPI.changeSource() is called by the customer
        //    This means that we can have two consecutive Event.MANUAL_SOURCE_CHANGE events in possible two orders.
        //
        //    AdapterAPI.changeSource() call is optional and should be called before the attempt for player source change!!
        //    It supposes to let us know that the player source change will be triggered, in case when we can't automatically
        //    detect it from the player events (e.g., HTML native adapter) and mostly to be able to pass the new
        //    AnalyticsConfig for the new source.
        //
        //    But because its call is implemented by the customer, we can't be sure about the order of these two events.
        // Solution:
        //    1) We want to mostly support the order `(optional) AdapterAPI.changeSource() -> player source change`.
        //    This should be communicated to the customer as the recommended way because it is more intuitive to call
        //    AdapterAPI.changeSource() before doing the player source change.
        //
        //    2) We also want to support "partially" the opposite order `player source change -> AdapterAPI.changeSource()`,
        //    BUT only in the case when the AdapterAPI.changeSource() is called while we are already in State.SOURCE_CHANGING,
        //    which means that the player source change has already started, and we are waiting for the source to be loaded.
        //    Why? Because in some customer integrations, the customer can't call AdapterAPI.changeSource() "proactively"
        //    (e.g., platforms like React Native, Chromecast) but rather "reactively" in response to the player event that
        //    we also relay on to detect the player source change. That means that the customer player event handler
        //    is called always after ours.
        ...whileIn(State.SOURCE_CHANGING).passOver(Event.MANUAL_SOURCE_CHANGE),
        {name: Event.READY, from: State.SOURCE_CHANGING, to: State.READY},
        // play attempt during sourceChanging
        {name: Event.PLAY, from: State.SOURCE_CHANGING, to: State.STARTUP},

        // Because of this state transition, we are able to make "a cut" in the current PLAYING / PAUSE state
        // and send "playing" / "pause" sample with the current custom data values. The new changed custom data values
        // will be set on upcoming samples after we transition back from the CUSTOMDATACHANGE state.
        // For any other states, we don't care, because InternalAdapter is not emitting the event.
        {name: Event.CUSTOM_DATA_CHANGE, from: [State.PLAYING, State.PAUSE], to: State.CUSTOMDATACHANGE},
        {name: Event.PLAYING, from: State.CUSTOMDATACHANGE, to: State.PLAYING},
        {name: Event.PAUSE, from: State.CUSTOMDATACHANGE, to: State.PAUSE},

        {name: Event.ERROR, from: getAllStatesBut([State.STARTUP, State.VIDEOSTART_FAILED], State), to: State.ERROR},

        // Special case to handle unload from any state
        // Transition is canceled in 'onbefore' callback and handled there.
        {name: Event.UNLOAD, from: '*', to: State.UNLOADED},
      ],

      // Keep callbacks sorted by order in which they are called by JSM library (and after order, sorted by name)
      //
      // Do not use convenience, shortened callbacks
      //  - instead of `onEVENT` use `onafterEVENT`
      //  - instead of `onSTATE` use `onenterSTATE`
      //
      // see: https://github.com/jakesgordon/javascript-state-machine/blob/2.4.0/README.md#callbacks
      callbacks: {
        [`onbefore${Event.UNLOAD}`]: (event, from, to, eventTimestamp: number, eventData: AnalyticsEventBase) => {
          this.stateMachineCallbacks.setVideoTimeEndFromEvent(eventData);
          const duration = eventTimestamp - this.onEnterStateTimestamp;

          if (from === State.STARTUP) {
            this.stateMachineCallbacks.videoStartFailed(duration, VideoStartFailedReason.PAGE_CLOSED);
          } else if (from === State.PLAYING) {
            this.stateMachineCallbacks.closedPlaying(duration);
          } else if (from === State.AD_PLAYBACK || from === State.AD_PLAYBACK_IN_STARTUP) {
            this.stateMachineCallbacks.closedAd(duration);
          }

          // Return false to cancel the event (and state transition) to avoid other 'onleaveXXX' callbacks calls.
          // We have to handle these special cases
          // - when the page is unloaded, the browser can't make a standard http requests (we need sendBeacon)
          // - when the player is destroyed, we can't access it to fill all sample properties
          return false;
        },

        [`onleave${State.AD_PLAYBACK_IN_STARTUP}`]: (
          event,
          from,
          to,
          eventTimestamp: number,
          eventData: AnalyticsEventBase,
        ) => {
          // To support the transition for the case: PREROLL AD
          this.stateMachineCallbacks.setVideoTimeEndFromEvent(eventData);
          const duration = eventTimestamp - this.onEnterStateTimestamp;
          this.stateMachineCallbacks.ad(duration, 'ad');
        },

        [`onleave${State.AD_PLAYBACK}`]: (event, from, to, eventTimestamp: number, eventData: AnalyticsEventBase) => {
          this.stateMachineCallbacks.setVideoTimeEndFromEvent(eventData);
          const duration = eventTimestamp - this.onEnterStateTimestamp;
          this.stateMachineCallbacks.ad(duration, 'ad');
        },

        [`onleave${State.CUSTOMDATACHANGE}`]: (
          _event,
          _from,
          _to,
          _eventTimestamp: number,
          eventData: CustomDataValuesEvent,
        ) => {
          this.stateMachineCallbacks.customdatachange(undefined, undefined, eventData);
        },

        [`onleave${State.PAUSE}`]: (event, from, to, eventTimestamp: number, eventData: AnalyticsEventBase) => {
          this.stateMachineCallbacks.setVideoTimeEndFromEvent(eventData);
          const duration = eventTimestamp - this.onEnterStateTimestamp;
          this.stateMachineCallbacks.pause(duration, 'pause');
        },

        [`onleave${State.PLAYING}`]: (event, from, to, eventTimestamp: number, eventData: AnalyticsEventBase) => {
          this.playingHeartbeatService.stopHeartbeat();

          this.stateMachineCallbacks.setVideoTimeEndFromEvent(eventData);
          const duration = eventTimestamp - this.onEnterStateTimestamp;
          this.stateMachineCallbacks.playing(duration, 'playing');
        },

        [`onleave${State.BUFFERING}`]: (event, from, to, eventTimestamp: number, eventData: AnalyticsEventBase) => {
          this.resetBufferingHelpers();

          this.stateMachineCallbacks.setVideoTimeEndFromEvent(eventData);
          const duration = eventTimestamp - this.onEnterStateTimestamp;
          this.stateMachineCallbacks.buffering(duration, 'buffering');
        },

        [`onleave${State.READY}`]: (event, from, to, _eventTimestamp: number, eventData: AnalyticsEventBase) => {
          // To support the fallback transition for the case: MISSING "PLAY" EVENT FROM A PLAY ATTEMPT DURING SLOW SOURCE CHANGE.
          if (event === Event.PLAYING && to === State.PLAYING) {
            this.stateMachineCallbacks.setVideoTimeEndFromEvent(eventData);
            this.stateMachineCallbacks.startup(1, 'startup');
            return;
          }
        },

        [`onleave${State.SEEKING}`]: (event, from, to, eventTimestamp: number, eventData: AnalyticsEventBase) => {
          this.stateMachineCallbacks.setVideoTimeEndFromEvent(eventData);
          const duration = eventTimestamp - this.onEnterStateTimestamp;
          this.stateMachineCallbacks.seeking(duration);
        },

        [`onleave${State.SETUP}`]: (event, from, to, eventTimestamp: number, eventData: AnalyticsEventBase) => {
          this.stateMachineCallbacks.setVideoTimeEndFromEvent(eventData);
          this.stateMachineCallbacks.setup();
        },

        [`onleave${State.STARTUP}`]: (event, from, to, eventTimestamp: number, eventData: AnalyticsEventBase) => {
          this.clearVideoStartTimeout();

          // if we are transitioning from STARTUP to READY state, it means that playback was not started (such as when
          // a user clicked pause during video loading), so we do not send a startup sample in that case
          if (to === State.READY) {
            return;
          }

          // To support the case: PREROLL AD
          // if we are transitioning from STARTUP to AD_PLAYBACK_IN_STARTUP state, it means that preroll ad has started,
          // so we do not want to send a startup sample in that case
          if (to === State.AD_PLAYBACK_IN_STARTUP) {
            return;
          }

          this.stateMachineCallbacks.setVideoTimeEndFromEvent(eventData);
          const duration = eventTimestamp - this.onEnterStateTimestamp;
          if (to === State.VIDEOSTART_FAILED) {
            this.stateMachineCallbacks.videoStartFailed(duration, createVideoStartFailedReason(event));
            return;
          }

          this.stateMachineCallbacks.startup(duration, 'startup');
          this.startupHasSucceeded = true;
        },

        onleavestate: (event, from, to) => {
          logStateMachineTransition('LEAVE', event, from, to);
        },

        [`onenter${State.PLAYING}`]: () => {
          this.playingHeartbeatService.startHeartbeat();
        },

        [`onenter${State.BUFFERING}`]: () => {
          this.startBufferingHeartbeatInterval();
        },

        [`onenter${State.SOURCE_CHANGING}`]: (_event, _from, _to, _eventTimestamp, eventData) => {
          this.stateMachineCallbacks.manualSourceChange(eventData);
          this.startupHasSucceeded = false;
        },

        [`onenter${State.STARTUP}`]: () => {
          this.setVideoStartTimeout();
        },

        [`onenter${State.QUALITY_CHANGE}`]: (_event, _from, _to, eventTimestamp, eventData: QualityChangeEvent) => {
          // Quality change should produce a "signal sample" and immediately transition us back to PLAYING state
          this.stateMachineCallbacks.qualitychange(eventData);
          this.stateMachine[Event.FINISH_QUALITY_CHANGE](eventTimestamp);
        },

        onenterstate: (event, from, to, eventTimestamp: number, eventData: AnalyticsEventBase) => {
          logStateMachineTransition('ENTER', event, from, to);

          this.onEnterStateTimestamp = eventTimestamp ?? Date.now();

          this.stateMachineCallbacks.setVideoTimeStartFromEvent(eventData);
        },

        [`onafter${Event.ERROR}`]: (_event, _from, _to, _eventTimestamp, eventData: ErrorEvent) => {
          this.stateMachineCallbacks.error(eventData);
        },

        [`onafter${Event.MANUAL_SOURCE_CHANGE}`]: (_event, from, to, _eventTimestamp, eventData) => {
          // To support the case: ADAPTER API SOURCE CHANGE VS. PLAYER SOURCE CHANGE
          // If the event was called while we are already in State.SOURCE_CHANGING, and we will stay there, it means:
          //  1) AdapterAPI.changeSource was called and now the "real" player source change is happening
          //  2) player source change was detected by InternalAdapter, and now the "later call" of AdapterAPI.changeSource
          //     is happening, so we should update the analytics config with the new one provided in the event data.
          if (from === State.SOURCE_CHANGING && to === State.SOURCE_CHANGING) {
            this.stateMachineCallbacks.manualSourceChange(eventData); // event data contains the new analytics config (if provided)
          }
        },
      },
    });
  }
}
